<script setup lang="ts">
import { useStore } from "vuex";
import { computed } from "vue";
import { addSizeToDataLayer } from "@/functions/analytics";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import ShapeInputFieldsWrapper from "@/components/UI/ShapeInputFieldsWrapper.vue";
import {Props} from "@/components/ShapeType.vue";

const store = useStore();
const { isCutout, action, prefix } = defineProps<Props>();

const dimensions = computed(() => store.getters.getMeasurements( 'rounded-rectangle', isCutout ) );
const dimensionLabel = computed(() => store.getters.dimensionLabel);
const dimensionMaxLabel = computed(() => store.getters.getMaxLabelForDimension);
const isDimensionValid = computed(() => store.getters.isShapeValid( isCutout ) );
const useIndividualCorners = computed(
	() => store.getters["rounded-rectangle/useIndividualCorners"]
);

const width = computed({
	get: () => dimensions.value.width,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "width",
			value: value,
		});
	},
});

const length = computed({
	get: () => dimensions.value.length,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "length",
			value: value,
		});
	},
});

const radius = computed({
	get: () => dimensions.value.radius,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "radius",
			value: value,
		});
	},
});

const topRightRadius = computed({
	get: () => dimensions.value.top_right_radius,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "top_right_radius",
			value: value,
		});
	},
});

const bottomRightRadius = computed({
	get: () => dimensions.value.bottom_right_radius,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "bottom_right_radius",
			value: value,
		});
	},
});

const bottomLeftRadius = computed({
	get: () => dimensions.value.bottom_left_radius,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "bottom_left_radius",
			value: value,
		});
	},
});

const topLeftRadius = computed({
	get: () => dimensions.value.top_left_radius,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "top_left_radius",
			value: value,
		});
	},
});
</script>
<template>
	<shape-input-fields-wrapper>
		<dimension-input
			inputRef="rounded-rectangle-length"
			:id="`${prefix}measurements-rounded-rectangle-length`"
			:name="`${prefix}measurements[dimensions][length]`"
			unit="cm"
			:label="dimensionLabel('width', 'rounded-rectangle')"
			:instructions="isCutout ? '' : dimensionMaxLabel('width')"
			:invalid="!isDimensionValid(`${prefix}rounded-rectangle-length`)"
			:onBlur="() => addSizeToDataLayer('length', +length, 'rounded-rectangle')"
			v-model="length"
		/>

		<dimension-input
			inputRef="rounded-rectangle-width"
			:id="`${prefix}measurements-rounded-rectangle-width`"
			:name="`${prefix}measurements[dimensions][width]`"
			unit="cm"
			:label="dimensionLabel('height', 'rounded-rectangle')"
			:instructions="isCutout ? '' : dimensionMaxLabel('height')"
			:invalid="!isDimensionValid(`${prefix}rounded-rectangle-width`)"
			:onBlur="() => addSizeToDataLayer('width', +width, 'rounded-rectangle')"
			v-model="width"
		/>

		<dimension-input
			v-if="useIndividualCorners === false"
			inputRef="rounded-rectangle-radius"
			id="measurements-rounded-rectangle-radius"
			name="measurements[dimensions][radius]"
			unit="cm"
			:label="dimensionLabel('radius', 'rounded-rectangle')"
			:invalid="!isDimensionValid('rounded-rectangle-radius')"
			:onBlur="() => addSizeToDataLayer('radius', +radius, 'rounded-rectangle')"
			v-model="radius"
		/>

		<dimension-input
			v-if="useIndividualCorners"
			inputRef="rounded-rectangle-top_left_radius"
			id="measurements-rounded-rectangle-top_left_radius"
			name="measurements[dimensions][top_left_radius]"
			unit="cm"
			:label="dimensionLabel('top_left_radius', 'rounded-rectangle')"
			:invalid="!isDimensionValid('rounded-rectangle-top_left_radius')"
			:onBlur="
				() =>
					addSizeToDataLayer(
						'top_left_radius',
						+topLeftRadius,
						'rounded-rectangle'
					)
			"
			v-model="topLeftRadius"
		/>

		<dimension-input
			v-if="useIndividualCorners"
			inputRef="rounded-rectangle-top_right_radius"
			id="measurements-rounded-rectangle-top_right_radius"
			name="measurements[dimensions][top_right_radius]"
			unit="cm"
			:label="dimensionLabel('top_right_radius', 'rounded-rectangle')"
			:invalid="!isDimensionValid('rounded-rectangle-top_right_radius')"
			:onBlur="
				() =>
					addSizeToDataLayer(
						'top_right_radius',
						+topRightRadius,
						'rounded-rectangle'
					)
			"
			v-model="topRightRadius"
		/>

		<dimension-input
			v-if="useIndividualCorners"
			inputRef="rounded-rectangle-bottom_left_radius"
			id="measurements-rounded-rectangle-bottom_left_radius"
			name="measurements[dimensions][bottom_left_radius]"
			unit="cm"
			:label="dimensionLabel('bottom_left_radius', 'rounded-rectangle')"
			:invalid="!isDimensionValid('rounded-rectangle-bottom_left_radius')"
			:onBlur="
				() =>
					addSizeToDataLayer(
						'bottom_left_radius',
						+bottomLeftRadius,
						'rounded-rectangle'
					)
			"
			v-model="bottomLeftRadius"
		/>

		<dimension-input
			v-if="useIndividualCorners"
			inputRef="rounded-rectangle-bottom_right_radius"
			id="measurements-rounded-rectangle-bottom_right_radius"
			name="measurements[dimensions][bottom_right_radius]"
			unit="cm"
			:label="dimensionLabel('bottom_right_radius', 'rounded-rectangle')"
			:invalid="!isDimensionValid('rounded-rectangle-bottom_right_radius')"
			:onBlur="
				() =>
					addSizeToDataLayer(
						'bottom_right_radius',
						+bottomRightRadius,
						'rounded-rectangle'
					)
			"
			v-model="bottomRightRadius"
		/>

		<input
			ref="rounded-rectangle-use_individual_corners"
			type="hidden"
			name="measurements[modifiers][use_individual_corners]"
			key="rounded-rectangle-use_individual_corners"
			:value="useIndividualCorners"
		/>
	</shape-input-fields-wrapper>
</template>
